import { useTheme } from '@/shared/context'
import { toGroupItems } from '@/shared/lib/menu-layout'
import { UiPopover } from '@/shared/ui'
import type { SiderMenuType } from '@/shared/ui'
import UiMenu from '@/shared/ui/ui-menu'
import { useMemo, useState } from 'react'

const getAncestorKeys = (itemList: any[], targetKey?: string): string[] => {
  if (!targetKey) return []
  for (const item of itemList) {
    if (item.key === targetKey) return []
    if (item.children) {
      if (item.children.some((c: any) => c.key === targetKey)) {
        return [item.key]
      }
      const deep = getAncestorKeys(item.children, targetKey)
      if (deep.length > 0) return [item.key, ...deep]
    }
  }
  return []
}

const MenuLeft = ({
  onChange,
  selectedKey,
  menu,
  collapsemenu,
  menuType = 'sub',
}: {
  onChange?: (menu: any) => void
  selectedKey?: string[]
  menu: any[]
  collapsemenu?: boolean
  menuType?: SiderMenuType
}) => {
  const { theme } = useTheme()
  // Quản lý các menu con đang xổ xuống. Tự động mở nhánh chứa mục đang chọn.
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    return selectedKey?.[0] ? getAncestorKeys(menu, selectedKey[0]) : []
  })

  // Tự động mở nhánh menu cha khi chuyển trang
  useMemo(() => {
    if (selectedKey?.[0]) {
      const ancestors = getAncestorKeys(menu, selectedKey[0])
      if (ancestors.length > 0) {
        setOpenKeys((prev) => Array.from(new Set([...prev, ...ancestors])))
      }
    }
  }, [selectedKey, menu])

  const onOpenChange = (keys: string[]) => {
    setOpenKeys(keys)
  }

  // Group header của antd không hiển thị được khi sider thu gọn — rơi về `sub` cho khỏi mất mục.
  const useGroup = menuType === 'group' && !collapsemenu
  const baseItems = useMemo(() => (useGroup ? toGroupItems(menu) : menu), [useGroup, menu])

  // Khi chưa click mở xổ xuống dưới (và sidebar không collapse), bọc label bằng UiPopover
  // để khi hover sẽ hiển thị menu con ở bên phải.
  const items = useMemo(() => {
    if (collapsemenu || useGroup) return baseItems

    const wrapItemsWithHoverDropdown = (itemList: any[]): any[] => {
      return (itemList ?? []).map((item) => {
        if (item?.children && item.children.length > 0) {
          const isOpen = openKeys.includes(item.key)
          const transformedChildren = wrapItemsWithHoverDropdown(item.children)

          // Nếu chưa xổ xuống dưới thì cho phép hover hiện ở bên phải
          const labelNode = !isOpen ? (
            <UiPopover
              content={
                <UiMenu
                  items={item.children}
                  onClick={onChange}
                  selectedKeys={selectedKey}
                  mode="vertical"
                  theme={theme}
                  style={{ border: 'none', background: 'transparent' }}
                />
              }
              placement="rightTop"
              trigger="hover"
              overlayClassName="sider-hover-submenu-popover"
              arrow={false}
              styles={{ container: { padding: 4 } }}
            >
              <span className="sider-menu-hover-trigger">{item.label}</span>
            </UiPopover>
          ) : (
            item.label
          )

          return {
            ...item,
            label: labelNode,
            children: transformedChildren,
          }
        }
        return item
      })
    }

    return wrapItemsWithHoverDropdown(baseItems)
  }, [baseItems, collapsemenu, useGroup, openKeys, onChange, selectedKey, theme])

  return (
    <UiMenu
      onClick={onChange}
      selectedKeys={selectedKey}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      mode="inline"
      items={items}
      className="menu-antd-user"
      theme={theme}
      inlineCollapsed={collapsemenu}
      style={{ borderInlineEnd: 'none' }}
    />
  )
}

export default MenuLeft
